/**
 * Liten typet EventBus for kryss-system-hendelser (fase 1, Mad TV-mekanikk).
 * GameState har sitt eget subscribe-mønster per hendelsestype; denne bussen
 * er for nye, løst koblede systemer (klokke, timeplaner, bakgrunnshendelser)
 * som ikke skal vokse GameState videre.
 */

type Lytter<T> = (payload: T) => void;

export class EventBus<M extends Record<string, unknown>> {
  private lyttere = new Map<keyof M, Set<Lytter<never>>>();

  /** Registrerer en lytter. Returnerer en unsubscribe-funksjon. */
  on<K extends keyof M>(hendelse: K, fn: Lytter<M[K]>): () => void {
    let sett = this.lyttere.get(hendelse);
    if (!sett) {
      sett = new Set();
      this.lyttere.set(hendelse, sett);
    }
    sett.add(fn as Lytter<never>);
    return () => this.off(hendelse, fn);
  }

  off<K extends keyof M>(hendelse: K, fn: Lytter<M[K]>): void {
    this.lyttere.get(hendelse)?.delete(fn as Lytter<never>);
  }

  emit<K extends keyof M>(hendelse: K, payload: M[K]): void {
    const sett = this.lyttere.get(hendelse);
    if (!sett) return;
    for (const fn of [...sett]) {
      try {
        (fn as Lytter<M[K]>)(payload);
      } catch (err) {
        console.warn(`[eventBus] lytter for ${String(hendelse)} kastet`, err);
      }
    }
  }
}
